import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DynamicModalService } from './dynamic-modal.service';
import { ModalConfig, ModalButton, ModalType, ModalFormField, ModalResult } from './dynamic-modal.models';
import { DynamicFormComponent } from '../../forms/dynamic-form/dynamic-form.component';
import { DynamicFormConfig, DynamicFormOutput } from '../../core/models/dynamic-form.models';

@Component({
  selector: 'is-dynamic-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, DynamicFormComponent],
  templateUrl: './dynamic-modal.component.html',
  styleUrls: ['./dynamic-modal.component.scss'],
})
export class DynamicModalComponent implements OnInit, OnDestroy {
  config: ModalConfig | null = null;
  formValues: Record<string, any> = {};
  dynamicFormConfig: DynamicFormConfig | null = null;

  @ViewChild('formRef') formRef?: DynamicFormComponent;

  private subscription?: Subscription;

  private readonly defaultButtons: Record<string, ModalButton[]> = {
    confirm: [
      { label: 'Cancelar', variant: 'outline-secondary', icon: 'bx-x', action: 'cancel' },
      { label: 'Confirmar', variant: 'danger', icon: 'bx-check', action: 'confirm' },
    ],
    form: [
      { label: 'Cancelar', variant: 'outline-secondary', action: 'cancel' },
      { label: 'Salvar', variant: 'primary', icon: 'bx-check', action: 'confirm' },
    ],
  };

  private readonly typeIcons: Record<ModalType, string> = {
    confirm: 'bx-question-mark',
    success: 'bx-check-circle',
    error: 'bx-error-circle',
    warning: 'bx-error',
    info: 'bx-info-circle',
    form: 'bx-edit',
    custom: 'bx-cog',
  };

  constructor(private modalService: DynamicModalService) {}

  ngOnInit(): void {
    this.subscription = this.modalService.config$.subscribe((config) => {
      this.config = config;
      this.dynamicFormConfig = null;

      if (config?.type === 'form' && config.formFields) {
        this.dynamicFormConfig = this.buildFormConfig(config.formFields);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  get icon(): string {
    return this.config?.icon || this.typeIcons[this.config?.type || 'info'];
  }

  get buttons(): ModalButton[] {
    return this.config?.buttons || this.defaultButtons[this.config?.type || ''] || [{ label: 'Ok', variant: 'primary', action: 'confirm' }];
  }

  get sizeClass(): string {
    const map = { sm: 'is-modal--sm', md: 'is-modal--md', lg: 'is-modal--lg' };
    return map[this.config?.size || 'sm'];
  }

  onButtonClick(button: ModalButton): void {
    let formValues: Record<string, any> | undefined;

    if (this.config?.type === 'form' && button.action === 'confirm' && this.formRef) {
      formValues = this.formRef.getValues();
    }

    const result: ModalResult = {
      action: button.action,
      customId: button.customId,
      formValues,
    };
    this.modalService.close(result);
  }

  onFormSubmit(output: DynamicFormOutput): void {
    if (output.valid) {
      this.modalService.close({ action: 'confirm', formValues: output.values });
    }
  }

  onBackdropClick(): void {
    if (this.config?.backdropDismiss !== false) {
      this.modalService.close({ action: 'cancel' });
    }
  }

  // ─── Private ────────────────────────────────────────────────────────────────

  private buildFormConfig(fields: ModalFormField[]): DynamicFormConfig {
    return {
      submitLabel: '',
      showCancel: false,
      columns: 12,
      fields: fields.map((f) => ({
        key: f.key,
        type: f.type as any,
        label: f.label,
        placeholder: f.placeholder,
        value: f.value,
        prefixIcon: f.prefixIcon,
        colSize: f.colSize || 12,
        validation: f.required ? { required: true } : undefined,
        options: f.options?.map((o) => ({ label: o.label, value: o.value })),
      })),
    };
  }
}
